import {
  Dialog,
  DialogContent,
  DialogTitle,
  V2Loader,
  VisuallyHidden,
} from "web-design-system";
import { getRouteApi, useRouter } from "@tanstack/react-router";
import { useMandate, useSIP } from "creso-core/hooks";
import { MandateItem, AmcLogo, PlanStatusBadge } from "web-core";
import capitalize from "lodash/capitalize";
import { convertToCurrency, formatAsPercentage } from "creso-core/utils";
import { format } from "date-fns";
import { StepupFrequency } from "creso-core/constants";

export function SipDetails() {
  const clientRoute = getRouteApi("/(auth)/_headed/clients/$id");

  const { client } = clientRoute.useLoaderData();

  const portfolioRoute = getRouteApi("/(auth)/_headed/clients/$id/portfolio");

  const { id = "" } = portfolioRoute.useSearch();

  const { data: sipData, isLoading: isLoadingSip } = useSIP({
    investmentAccountId: client?.investment_account ?? "",
    id: id as string,
  });

  const { data: linkedMandate, isLoading: isLoadingMandate } = useMandate({
    investmentAccountGuid: client?.investment_account ?? "",
    id: sipData?.mandate_id ?? "",
  });

  const router = useRouter();

  return (
    <Dialog open onOpenChange={(open) => !open && router.history.back()}>
      <DialogContent className="w-110">
        <VisuallyHidden>
          <DialogTitle>SIP Details</DialogTitle>
        </VisuallyHidden>
        <V2Loader
          isLoading={isLoadingSip || isLoadingMandate}
          className="flex flex-col"
        >
          <div className="flex items-center pb-5 gap-2 border-b border-border">
            <AmcLogo
              size="xs"
              amc_code={sipData?.amc_morningstar_code ?? null}
            />
            <span className="line-clamp-1 font-semibold">
              {sipData?.scheme_name ?? ""}
            </span>
            <PlanStatusBadge status={sipData?.status ?? "ACTIVE"} />
          </div>
          <div className="py-5 flex flex-col gap-3">
            <span className="font-semibold text-sm">Installment details</span>
            <div className="grid grid-cols-2 gap-5">
              <div className="flex flex-col gap-1">
                <span className="text-sm">Frequency</span>
                <span className="text-sm font-semibold">
                  {capitalize(sipData?.frequency) ?? "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm">Amount</span>
                <span className="text-sm font-semibold">
                  {convertToCurrency(sipData?.installment_amount ?? 0)}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm">Start date</span>
                <span className="text-sm font-semibold">
                  {sipData?.start_date
                    ? format(sipData.start_date ?? "", "PP")
                    : "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm">Next installment</span>
                <span className="text-sm font-semibold">
                  {sipData?.installment_due_date
                    ? format(sipData?.installment_due_date ?? "", "PP")
                    : "-"}
                </span>
              </div>
              {sipData?.step_up_frequency && (
                <>
                  <div className="col-span-2 h-px bg-border" />
                  <div className="flex flex-col gap-1">
                    <span className="text-sm">Step up value</span>
                    <span className="text-sm font-semibold">
                      {sipData?.step_up_amount
                        ? convertToCurrency(sipData?.step_up_amount ?? "")
                        : sipData?.step_up_percentage
                          ? formatAsPercentage(sipData?.step_up_percentage)
                          : "-"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm">Frequency</span>
                    <span className="text-sm font-semibold">
                      {sipData?.step_up_frequency === StepupFrequency.YEARLY
                        ? "Yearly"
                        : "Half-yearly"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm">Next step-up date</span>
                    <span className="text-sm font-semibold">
                      {sipData?.next_step_up_date
                        ? format(sipData?.next_step_up_date ?? "", "PP")
                        : "-"}
                    </span>
                  </div>
                </>
              )}
              <div className="col-span-2 h-px bg-border" />
              <div className="flex flex-col gap-1">
                <span className="text-sm">Installments completed</span>
                <span className="text-sm font-semibold">
                  {sipData?.no_of_installments_paid ?? "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm">Total invested via SIP</span>
                <span className="text-sm font-semibold">
                  {convertToCurrency(
                    sipData?.total_installment_amount_paid ?? 0
                  )}
                </span>
              </div>
            </div>
            {sipData?.status === "PAUSED" && sipData?.resume_date && (
              <p className="text-xs text-muted-foreground mt-2">
                SIP will auto-resume on{" "}
                {format(sipData?.resume_date ?? "", "PP")} if not resumed
                manually.
              </p>
            )}
          </div>
          {linkedMandate && (
            <div className="py-5 flex border-t border-border flex-col gap-3">
              <span className="text-sm font-semibold">Linked Mandate</span>
              <div className="rounded-md bg-card p-5 border border-border">
                <MandateItem mandate={linkedMandate} />
              </div>
            </div>
          )}
        </V2Loader>
      </DialogContent>
    </Dialog>
  );
}
